// ══════════════════════════════════════════════════════════════
//  RaumbuchPanel — Raumbuch mit View-Toggle (Tabelle/Split/Scan)
//  Liest ExtrudedPolygonVolumes aus window._clipExtrudedVolumes,
//  berechnet Scan-Daten (Fläche, Höhe, Volumen, Umfang) und
//  synchronisiert Selection ↔ 3D-Viewer.
// ══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo } from 'react';
import TablePanel from './TablePanel';
import ScanPanel from './ScanPanel';
import { ViewToggle } from './ViewToggle';
import type { ViewMode, ExportFormat } from './ViewToggle';
import { RAUMBUCH_COLUMNS } from './tableColumns';
import type { RaumbuchRow } from './tableColumns';
import { exportCSV, exportDXF } from './exportUtils';

// ── Potree Volume Interface (duck-type, kein Import nötig) ──
interface PotreeVolume {
  id: number;
  label: string;
  name: string;
  profile: { x: number; y: number }[];
  position: { x: number; y: number; z: number };
  scale: { x: number; y: number; z: number };
  _floorId: string | null;
  _usage: string;
  _notes: string;
  _volumeType: string;
  _faceMesh?: unknown;
  wireframe?: unknown;
  activate?: () => void;
  deactivate?: () => void;
  _raumbuch?: Record<string, string>;
}

// ── Geometrie-Berechnung aus Profil+Scale ────────────────────

function computeArea(profile: { x: number; y: number }[], scaleX: number, scaleY: number): number {
  let a = 0;
  const n = profile.length;
  for (let i = 0; i < n; i++) {
    const cur = profile[i];
    const next = profile[(i + 1) % n];
    a += cur.x * next.y - next.x * cur.y;
  }
  return Math.abs(a / 2) * scaleX * scaleY;
}

function computePerimeter(profile: { x: number; y: number }[], scaleX: number, scaleY: number): number {
  let p = 0;
  const n = profile.length;
  for (let i = 0; i < n; i++) {
    const cur = profile[i];
    const next = profile[(i + 1) % n];
    const dx = (next.x - cur.x) * scaleX;
    const dy = (next.y - cur.y) * scaleY;
    p += Math.sqrt(dx * dx + dy * dy);
  }
  return p;
}

function floorLabel(floorId: string | null): string {
  const map: Record<string, string> = {
    UG2: 'UG2', UG1: 'UG1', EG: 'EG',
    OG1: 'OG1', OG2: 'OG2', OG3: 'OG3', DG: 'DG',
  };
  return floorId ? (map[floorId] || floorId) : '—';
}

// ── Volume → Row Konvertierung ───────────────────────────────

function volumeToRow(vol: PotreeVolume): RaumbuchRow {
  const area = computeArea(vol.profile, Math.abs(vol.scale.x), Math.abs(vol.scale.y));
  const height = Math.abs(vol.scale.z);
  const volume = area * height;
  const perimeter = computePerimeter(vol.profile, Math.abs(vol.scale.x), Math.abs(vol.scale.y));
  const rb = vol._raumbuch || {};

  return {
    id: vol.id,
    volumeId: vol.id,
    values: {
      nr: rb.nr || vol.label,
      name: vol.name || '',
      ebene: floorLabel(vol._floorId),
      flaeche: area.toFixed(2),
      hoehe: height.toFixed(2),
      volumen: volume.toFixed(2),
      umfang: perimeter.toFixed(2),
      fenster: rb.fenster || '—',
      tueren: rb.tueren || '—',
      nutzung: vol._usage || rb.nutzung || '',
      boden: rb.boden || '',
      wand: rb.wand || '',
      decke: rb.decke || '',
      schadstoffe: rb.schadstoffe || '—',
      brandschutz: rb.brandschutz || '',
      heizung: rb.heizung || '',
      lux: rb.lux || '',
      owner: rb.owner || '',
      faellig: rb.faellig || '',
      status: rb.status || 'Offen',
    },
  };
}

// ── Hook: Potree-Volumes als Rows ────────────────────────────

function usePotreeRows(): [RaumbuchRow[], () => void] {
  const [rows, setRows] = useState<RaumbuchRow[]>([]);

  const refresh = useCallback(() => {
    const vols: PotreeVolume[] = (window as any)._clipExtrudedVolumes || [];
    const roomVols = vols.filter(v => v._volumeType === 'room' && !((v as any)._isClipProxy));
    setRows(roomVols.map(volumeToRow));
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 2000);
    const handler = () => refresh();
    window.addEventListener('orto:roomsChanged', handler);
    return () => {
      clearInterval(timer);
      window.removeEventListener('orto:roomsChanged', handler);
    };
  }, [refresh]);

  return [rows, refresh];
}

// ── Hauptkomponente ──────────────────────────────────────────

export default function RaumbuchPanel() {
  const [rows, refreshRows] = usePotreeRows();
  const [selectedRows, setSelectedRows] = useState<Set<number>>(() => new Set());
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('split');

  // ── Selektierte Volumes (für ScanPanel, multi-select) ──
  const selectedVolumeIds = useMemo(() => {
    const ids = new Set<number>();
    selectedRows.forEach(idx => {
      const vid = rows[idx]?.volumeId;
      if (vid != null) ids.add(vid);
    });
    return ids;
  }, [selectedRows, rows]);

  // Single ID for backward compat (Potree activation, scan card)
  const selectedVolumeId = selectedVolumeIds.size > 0
    ? [...selectedVolumeIds][selectedVolumeIds.size - 1]
    : null;

  const hoverVolumeId = hoverIdx != null ? (rows[hoverIdx]?.volumeId ?? null) : null;

  // ── Potree-Sichtbarkeit steuern ──
  useEffect(() => {
    if (viewMode === 'table') return; // In table mode, AppShell overlay covers Potree

    // In split/scan: Potree-Container anpassen
    const container = document.querySelector('.potree_container') as HTMLElement;
    const shell = document.getElementById('viewer-shell');
    if (!container || !shell) return;

    if (viewMode === 'split') {
      // Potree links, halbe Breite (nach Sidebar)
      container.style.width = 'calc(50% - var(--nav-rail-w, 60px) / 2)';
    }
    // scan: Potree volle Breite (default)

    // Resize triggern damit Potree sich anpasst
    const raf = requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });

    return () => {
      cancelAnimationFrame(raf);
      container.style.width = '';
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    };
  }, [viewMode]);

  // ── Row-Toggle (Checkbox) ──
  const handleToggleRow = useCallback((idx: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  // ── Cell-Edit → Volume updaten ──
  const handleCellChange = useCallback((rowId: number, key: string, value: string) => {
    const vols: PotreeVolume[] = (window as any)._clipExtrudedVolumes || [];
    const vol = vols.find(v => v.id === rowId);
    if (!vol) return;

    if (key === 'name') { vol.name = value; }
    else if (key === 'nutzung') { vol._usage = value; }
    else if (key === 'notes') { vol._notes = value; }
    else {
      if (!vol._raumbuch) vol._raumbuch = {};
      vol._raumbuch[key] = value;
    }

    if (typeof (window as any)._autoSaveRooms === 'function') {
      (window as any)._autoSaveRooms();
    }
    window.dispatchEvent(new CustomEvent('orto:roomsChanged'));
    refreshRows();
  }, [refreshRows]);

  // ── Row-Klick → Kameraflug zum Volume ──
  const handleRowClick = useCallback((rowId: number) => {
    const vols: PotreeVolume[] = (window as any)._clipExtrudedVolumes || [];
    const vol = vols.find(v => v.id === rowId);
    if (!vol) return;

    if (typeof (window as any).activateVolume === 'function') {
      (window as any).activateVolume(vol);
    }
    window.dispatchEvent(new CustomEvent('orto:flyToVolume', { detail: { volumeId: rowId } }));
  }, []);

  // ── ScanPanel → Tabelle: Volume toggle (multi-select) ──
  const handleScanToggle = useCallback((volumeId: number) => {
    const idx = rows.findIndex(r => r.volumeId === volumeId);
    if (idx >= 0) {
      setSelectedRows(prev => {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
        return next;
      });
    }
  }, [rows]);

  const handleScanDeselectAll = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  // ── ScanPanel → Tabelle: Hover ──
  const handleScanHover = useCallback((volumeId: number | null) => {
    if (volumeId == null) { setHoverIdx(null); return; }
    const idx = rows.findIndex(r => r.volumeId === volumeId);
    setHoverIdx(idx >= 0 ? idx : null);
  }, [rows]);

  // ── 3D → Tabelle: Volume im Viewer selektiert ──
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.volumeId) {
        const idx = rows.findIndex(r => r.volumeId === detail.volumeId);
        if (idx >= 0) setSelectedRows(new Set([idx]));
      }
    };
    window.addEventListener('orto:volumeSelected', handler);
    return () => window.removeEventListener('orto:volumeSelected', handler);
  }, [rows]);

  // ── Export-Handler ──
  const handleExport = useCallback((format: ExportFormat) => {
    // Selektierte Rows exportieren, falls vorhanden — sonst alle
    const exportRows = selectedRows.size > 0
      ? rows.filter((_, i) => selectedRows.has(i))
      : rows
    if (format === 'csv') exportCSV(exportRows)
    else if (format === 'dxf') exportDXF(exportRows)
  }, [rows, selectedRows])

  // ── Hover-Sync: Tabelle → 3D ──
  useEffect(() => {
    if (hoverIdx == null) return;
    const row = rows[hoverIdx];
    if (!row?.volumeId) return;
    window.dispatchEvent(new CustomEvent('orto:volumeHover', { detail: { volumeId: row.volumeId } }));
    return () => {
      window.dispatchEvent(new CustomEvent('orto:volumeHover', { detail: { volumeId: null } }));
    };
  }, [hoverIdx, rows]);

  // ── Render: Tabelle ──
  const tableEl = (
    <TablePanel
      columns={RAUMBUCH_COLUMNS}
      rows={rows}
      selectedRows={selectedRows}
      onToggleRow={handleToggleRow}
      onCellChange={handleCellChange}
      onRowClick={handleRowClick}
      hoverIdx={hoverIdx}
      onHoverIdx={setHoverIdx}
    />
  );

  // ── Render: ScanPanel ──
  const scanEl = (
    <ScanPanel
      selectedVolumeIds={selectedVolumeIds}
      hoverVolumeId={hoverVolumeId}
      onToggleVolume={handleScanToggle}
      onDeselectAll={handleScanDeselectAll}
      onHoverVolume={handleScanHover}
    />
  );

  // ── Compact Scan-Info Card (für Scan-Fullscreen) ──
  const selectedRow = selectedVolumeId != null
    ? rows.find(r => r.volumeId === selectedVolumeId)
    : null;

  const scanInfoCard = selectedRow ? (
    <div className="rb-scan-card">
      <div className="rb-scan-card__title">{selectedRow.values.nr} — {selectedRow.values.name || 'Unbenannt'}</div>
      <div className="rb-scan-card__grid">
        <span className="rb-scan-card__label">Fläche</span>
        <span className="rb-scan-card__value">{selectedRow.values.flaeche} m²</span>
        <span className="rb-scan-card__label">Höhe</span>
        <span className="rb-scan-card__value">{selectedRow.values.hoehe} m</span>
        <span className="rb-scan-card__label">Volumen</span>
        <span className="rb-scan-card__value">{selectedRow.values.volumen} m³</span>
        <span className="rb-scan-card__label">Nutzung</span>
        <span className="rb-scan-card__value">{selectedRow.values.nutzung || '—'}</span>
        <span className="rb-scan-card__label">Ebene</span>
        <span className="rb-scan-card__value">{selectedRow.values.ebene}</span>
        <span className="rb-scan-card__label">Status</span>
        <span className="rb-scan-card__value">{selectedRow.values.status}</span>
      </div>
    </div>
  ) : null;

  // ── Layout ────────────────────────────────────────────────
  return (
    <div className={`rb-root rb-root--${viewMode}`}>
      <ViewToggle mode={viewMode} onChange={setViewMode} rowCount={rows.length} onExport={handleExport} />

      {viewMode === 'table' && (
        <div className="rb-layout rb-layout--table">
          {tableEl}
        </div>
      )}

      {viewMode === 'split' && (
        <div className="rb-layout rb-layout--split">
          <div className="rb-layout__table">
            {tableEl}
          </div>
          <div className="rb-layout__scan">
            {scanEl}
          </div>
        </div>
      )}

      {viewMode === 'scan' && (
        <div className="rb-layout rb-layout--scan">
          {scanEl}
          {scanInfoCard}
        </div>
      )}
    </div>
  );
}
